import { cn } from "@/lib/utils"

/**
 * Master switch for the black announcement bar above the navbar. Off since
 * 2026-09-04 (Eugene) — the copy it carried is no longer current, but the bar
 * is expected back, so this is a flag rather than deleted markup.
 *
 * Flipping it to `true` is the ONLY change needed: `PageShell` both renders
 * the bar and hands this value to the navbar for its SSR resting offset, and
 * the navbar measures `[data-top-banner]` on the client, so a bar of any
 * height positions the transparent nav correctly with no constant to update.
 */
export const TOP_BANNER_ENABLED = false

export interface TopBannerProps {
  message: string
  messageShort?: string
  linkText?: string
  linkHref?: string
  onLinkClick?: () => void
  onDismiss?: () => void
}

export function TopBanner({
  message,
  messageShort,
  linkText,
  linkHref,
  onLinkClick,
  onDismiss,
}: TopBannerProps) {
  const icon = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 25 32"
      fill="none"
      className="flex-shrink-0 text-neutral-500"
      aria-hidden="true"
    >
      <path
        d="M10.714 21.333L14.286 21.333L14.286 16.86L18.75 16.86L18.75 13.282L14.286 13.282L14.286 8.809L10.714 8.809L10.714 13.282L6.25 13.282L6.25 16.86L10.714 16.86ZM12.5 32C8.913 30.933 5.932 28.756 3.559 25.467C1.186 22.179 0 18.475 0 14.355L0 4.679L12.5 0L25 4.679L25 14.355C25 18.475 23.814 22.179 21.441 25.467C19.068 28.756 16.088 30.933 12.5 32Z"
        fill="currentColor"
      />
    </svg>
  )

  return (
    <div
      // Measured by the navbar (see TOP_BANNER_ENABLED) to place the
      // transparent nav directly under the bar, whatever its height.
      data-top-banner=""
      className={cn(
        "relative z-40 flex w-full items-center justify-center bg-neutral-950 px-4 py-2 text-sm text-neutral-300"
      )}
    >
      {/* Mobile: entire banner is one tappable target (link or modal trigger) */}
      {messageShort && onLinkClick && (
        <button
          type="button"
          onClick={onLinkClick}
          className="flex items-center justify-center gap-1.5 transition-opacity hover:opacity-80 md:hidden"
        >
          {icon}
          <span>{messageShort}</span>
        </button>
      )}
      {messageShort && !onLinkClick && linkHref && (
        <a
          href={linkHref}
          className="flex items-center justify-center gap-1.5 transition-opacity hover:opacity-80 md:hidden"
        >
          {icon}
          <span>{messageShort}</span>
        </a>
      )}

      {/* Desktop: message + separate link */}
      <p
        className={cn(
          "items-center justify-center gap-1.5 text-center",
          messageShort ? "hidden md:flex" : "flex"
        )}
      >
        {icon}
        {message}
        {linkText &&
          (onLinkClick ? (
            <>
              {" · "}
              <button
                type="button"
                onClick={onLinkClick}
                className="underline decoration-neutral-600 underline-offset-2 transition-opacity hover:opacity-80"
              >
                {linkText}
              </button>
            </>
          ) : (
            linkHref && (
              <>
                {" · "}
                <a
                  href={linkHref}
                  className="underline decoration-neutral-600 underline-offset-2 transition-opacity hover:opacity-80"
                >
                  {linkText}
                </a>
              </>
            )
          ))}
      </p>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-1/2 right-4 -translate-y-1/2 text-white/70 transition-colors hover:text-white"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 4L4 12M4 4l8 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  )
}
